import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ExternalLink, Flag } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { linkReports } from "@/lib/api";
import {
  getSkipExternalLinkConfirm,
  setSkipExternalLinkConfirm,
  subscribeSkipExternalLinkConfirm,
} from "@/lib/externalLinkPrefs";

/**
 * A11y-safe outbound-link launcher.
 *
 * Clicking the pill opens a confirmation dialog naming the destination
 * host + a short reminder that the site is external. The user must
 * explicitly confirm before we call `window.open`.
 *
 * Users can opt-in to "Don't ask again this session" — the choice lives
 * in a module-level flag (not localStorage) so it clears when the tab
 * closes. The dialog also carries a "Report bad link" chip that POSTs to
 * /api/link-reports so operators can rotate stale sources out of
 * `plantLinks.js`.
 *
 * Props:
 *   - href      : full https URL (required)
 *   - label     : visible pill text (required)
 *   - source    : display name for the dialog title (e.g. "USDA PLANTS")
 *                 defaults to the hostname parsed from `href`.
 *   - testId    : outer button data-testid
 *   - scanId    : optional plant scan_id → adds context to bad-link report
 *   - plantName : optional common / scientific name → same
 */
export const ExternalLinkConfirm = ({
  href,
  label,
  source,
  testId,
  children,
  scanId,
  plantName,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dontAsk, setDontAsk] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  // Mirror the session flag so pills across the page share state — one
  // checkbox click on any pill immediately suppresses the dialog for the
  // rest of the tab lifetime.
  const [skip, setSkip] = useState(() => getSkipExternalLinkConfirm());
  useEffect(() => subscribeSkipExternalLinkConfirm(setSkip), []);

  if (!href) return null;

  let host = source;
  if (!host) {
    try { host = new URL(href).hostname.replace(/^www\./, ""); } catch { host = href; }
  }

  const openTarget = () => {
    // noopener + noreferrer prevents the destination from touching window.opener.
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const onPillClick = () => {
    if (skip) {
      openTarget();
      return;
    }
    setDontAsk(false); // reset so a prior dialog's toggle doesn't leak
    setReported(false);
    setOpen(true);
  };

  const confirmAndOpen = () => {
    if (dontAsk) setSkipExternalLinkConfirm(true);
    setOpen(false);
    openTarget();
  };

  const reportBadLink = async () => {
    if (reported || reporting) return;
    setReporting(true);
    try {
      let hostForReport = "";
      try { hostForReport = new URL(href).hostname.replace(/^www\./, ""); } catch { hostForReport = ""; }
      await linkReports.submit({
        url: href,
        host: hostForReport,
        source: source || host || "",
        scan_id: scanId,
        plant_name: plantName,
      });
      setReported(true);
      toast.success(t("externalLink.reportThanks"));
    } catch {
      toast.error(t("externalLink.reportFail"));
    } finally {
      setReporting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onPillClick}
        data-testid={testId}
        className="inline-flex items-center gap-1.5 rounded-full bg-parchment hover:bg-[#EEE9DE] text-leaf-ink text-xs px-3 py-1.5 transition"
      >
        {children || label}
        <ExternalLink className="w-3 h-3 opacity-70" strokeWidth={1.5} aria-hidden="true" />
      </button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="external-link-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("externalLink.title", { source: host })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("externalLink.body", { host })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p
            className="text-[11px] leading-snug text-leaf-mist bg-parchment/60 border border-[#E8E5DF] rounded-xl px-3 py-2 truncate"
            data-testid="external-link-url"
            title={href}
          >
            {href}
          </p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-leaf-ink cursor-pointer select-none">
              <Checkbox
                checked={dontAsk}
                onCheckedChange={(v) => setDontAsk(Boolean(v))}
                data-testid="external-link-dont-ask"
                aria-label={t("externalLink.dontAsk")}
              />
              <span>{t("externalLink.dontAsk")}</span>
            </label>
            <button
              type="button"
              onClick={reportBadLink}
              disabled={reporting || reported}
              data-testid="external-link-report"
              className="inline-flex items-center gap-1 text-[11px] text-leaf-mist hover:text-rust disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              <Flag className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
              {reported ? t("externalLink.reportedShort") : t("externalLink.reportBad")}
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="external-link-cancel">
              {t("externalLink.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAndOpen} data-testid="external-link-continue">
              {t("externalLink.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExternalLinkConfirm;
